import { forwardRef, useCallback, useMemo } from 'react';
import { currencyFormatter as currencyFormatterJs } from '../../QuotePage/content/QuoteGrids/utils';
import { Grid, type Row } from '../tabParts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: QuoteGrids/utils.js formats an AG Grid value
const currencyFormatter = currencyFormatterJs as any as (args: { field: unknown; withDecimals?: boolean }) => string;

type FeeRow = { name: string; rateOfNet: number; amount: number; isEditable?: boolean; id?: unknown };
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: AG Grid cell params
type CellParams = any;

const calculateAmount = (rateOfNet: number, totalGrossPremium: number) => (rateOfNet ? (Number(rateOfNet) / 100) * Number(totalGrossPremium) : 0);

const formatPercentage = (value: number) => {
  if (value === 0 || isNaN(value)) {
    return 'N/A';
  }
  const formattedValue = Number(value).toFixed(4);
  if (formattedValue === '0.00' && value > 0) {
    return '< 0.01%';
  }
  return `${formattedValue}%`;
};

/** A temporary-locations group's premium, fees and total, with each fee's rate and cost editable. */
export const TemporaryLocationsFees = forwardRef<
  unknown,
  {
    policy: Row;
    setFeeGridRef: (ref: unknown) => void;
    onFeeUpdate: (fees: FeeRow[]) => void;
    feeGridRef?: unknown;
    endorsementData?: Row;
    onSelectionChanged?: unknown;
    setGridReference?: unknown;
  }
>(({ policy, setFeeGridRef, onFeeUpdate, feeGridRef, endorsementData = {} }) => {
  const { premium = 0 } = endorsementData;

  const feeRows: FeeRow[] = policy?.fees
    ? policy?.fees?.map((fee: Row) => ({ name: fee.name, rateOfNet: fee?.rateOfNet || 0, amount: fee.amount, isEditable: true, id: fee.id }))
    : [];

  const totalCost = premium + feeRows?.reduce((sum, row) => sum + row.amount, 0);
  const totalPremium = feeRows?.reduce((sum, row) => sum + row.rateOfNet, 0) + (premium / policy.netPremium) * 100;

  const setGridValues = useCallback(
    (params: CellParams) => {
      const { data, newValue } = params;
      const { id } = data;
      const allFees = policy?.fees;
      const totalGrossPremium = premium;
      const editedFieldMap: Record<string, string> = { fee: 'name', amount: 'amount', rateOfNet: 'rateOfNet' };
      const editedField = editedFieldMap[params.colDef.field] || '';
      const rowIndex = allFees.findIndex((fee: Row) => fee.id === id);
      const value = params.colDef.dataType === 'number' ? parseFloat(newValue) : newValue;
      if (rowIndex === -1) return;
      allFees[rowIndex][editedField] = isNaN(value) && editedField !== 'name' ? 0 : value;
      const updatedFee = { ...allFees[rowIndex] };
      if (editedField === 'amount') {
        updatedFee.rateOfNet = null;
      } else if (editedField === 'rateOfNet') {
        const newAmount = !isNaN(value) ? Number(calculateAmount(value, totalGrossPremium).toFixed(2)) : updatedFee.amount;
        if (value && !isNaN(value)) {
          updatedFee.amount = newAmount;
        }
      } else if (updatedFee[editedField]) updatedFee[editedField] = value;

      const updatedFees = [...feeRows];
      updatedFees[rowIndex] = updatedFee;
      onFeeUpdate(updatedFees);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [policy]
  );

  const colDefs = useMemo(() => {
    const flashAndSet = (params: CellParams) => {
      const oldValue = params.data[params.colDef.field];
      if (oldValue === params.newValue) return false;
      params.api.flashCells({ rowNodes: [params.node], columns: ['amount', 'rateOfNet'] });
      setGridValues(params);
      return true;
    };
    return [
      {
        headerName: 'Summary of Costs',
        field: 'name',
        width: 250,
        editable: (params: CellParams) => params.data.isEditable,
        minWidth: 300,
        valueSetter: (params: CellParams) => {
          setGridValues(params);
          return true;
        },
      },
      {
        headerName: 'Percentage of Premium',
        field: 'rateOfNet',
        editable: (params: CellParams) => params.data.isEditable,
        valueFormatter: (params: CellParams) => (params.data ? formatPercentage(params.data.rateOfNet) : ''),
        valueSetter: flashAndSet,
      },
      {
        headerName: 'Cost',
        field: 'amount',
        editable: (params: CellParams) => params.data.isEditable,
        valueFormatter: (params: CellParams) => (params.value ? currencyFormatter({ field: { value: params.value }, withDecimals: true }) : 'N/A'),
        valueSetter: flashAndSet,
      },
    ];
  }, [setGridValues]);

  const rowData = [
    { name: 'Premium', rateOfNet: (premium / policy.netPremium) * 100, amount: premium },
    ...feeRows,
    { name: 'Total', rateOfNet: totalPremium, amount: totalCost, isEditable: false },
  ];

  return (
    <div className="grid-container mb-2.5 w-full">
      <Grid
        data={rowData}
        columns={colDefs}
        floatingFilter={false}
        setGridReference={setFeeGridRef}
        shouldShowPagination={false}
        undoRedoCellEditing={true}
        undoRedoCellEditingLimit={100}
        rowSelection="multiple"
        gridRef={feeGridRef}
      />
    </div>
  );
});
TemporaryLocationsFees.displayName = 'TemporaryLocationsFees';
